import { i18n } from '@lingui/core'

import type { LocaleName } from '@/models'
import { getLocaleName } from '@/stores/setting.store'

/**
 * Lingui i18n singleton.
 *
 * The initial locale is activated synchronously from the Zustand setting store.
 * Subsequent locale switches use dynamic imports of compiled message catalogs.
 */
export { i18n }

/**
 * Dynamically load and activate a locale catalog.
 * Uses Vite dynamic import — the @lingui/vite-plugin compiles .po files on the fly.
 */
export async function dynamicActivate(locale: LocaleName): Promise<void> {
  const { messages } = await import(`../../locales/${locale}/messages.po`)
  i18n.load(locale, messages)
  i18n.activate(locale)
}

/**
 * Switch to a different locale — guards against redundant switches.
 */
export async function switchLocale(locale: LocaleName): Promise<void> {
  if (i18n.locale === locale) return
  await dynamicActivate(locale)
}

/** Supported Lingui locales. */
export const locales: readonly LocaleName[] = ['en-GB', 'zh-CN', 'cy-GB'] as const

export type SupportedLocale = (typeof locales)[number]

/**
 * Initialize the Lingui i18n instance on first client render.
 * Automatically detects the locale from the Zustand setting store.
 */
export async function initLinguiI18n(): Promise<void> {
  const initialLocale = getLocaleName()
  await dynamicActivate(initialLocale)
}
